import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Smile, Image as ImageIcon, Mic, Check, CheckCheck, Sparkles, Eye, Heart, MessageCircle, TrendingUp, Briefcase, Bookmark, ArrowUpRight } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';
import AnimatedNumber from './AnimatedNumber';

const messages = [
  { from: 'them', text: 'Oi Beatriz! Adorei sua cafeteria ☕ Quero entender melhor seu projeto.', time: '14:02' },
  { from: 'me', text: 'Oii Roberto! 😊 Que ótimo! Quer marcar uma call essa semana?', time: '14:03' },
  { from: 'them', text: 'Perfeito. Estou pensando em entrar com R$ 80k pra ajudar a abrir as 2 novas unidades.', time: '14:05' },
  { from: 'me', text: 'Sério?! 🤩 Isso seria incrível. Já tenho o plano de expansão pronto.', time: '14:06' },
];

const ChatDashboardSection = () => {
  const [view, setView] = useState('entrepreneur'); // 'entrepreneur' | 'investor'
  const [typedCount, setTypedCount] = useState(messages.length);

  return (
    <section id="app" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(79,172,254,0.12), transparent 70%)'
        }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,167,38,0.12), transparent 70%)'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Dentro do app"
          title={<>Chat privado e seu próprio <span className="gradient-text">painel</span>.</>}
          subtitle="Depois que a Spark acontece, vocês conversam direto e cada um acompanha tudo no seu painel."
        />

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
          {/* LEFT — Chat */}
          <ChatMockup />

          {/* RIGHT — Dashboard with role toggle */}
          <div>
            <RoleToggle view={view} setView={setView} />
            <AnimatePresence mode="wait">
              {view === 'entrepreneur' ? (
                <motion.div
                  key="entrep"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.4 }}
                >
                  <EntrepreneurDashboard />
                </motion.div>
              ) : (
                <motion.div
                  key="invest"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <InvestorDashboardMock />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

const RoleToggle = ({ view, setView }) => {
  return (
    <div className="glass-card rounded-full p-1 inline-flex items-center mb-6">
      <button
        onClick={() => setView('entrepreneur')}
        className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${
          view === 'entrepreneur'
            ? 'bg-gradient-to-r from-[#ffa726] to-[#ff7043] text-black shadow-lg shadow-[#ff7043]/30'
            : 'text-slate-400 hover:text-white'
        }`}
      >
        Empreendedor
      </button>
      <button
        onClick={() => setView('investor')}
        className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${
          view === 'investor'
            ? 'bg-gradient-to-r from-[#4facfe] to-[#1e90ff] text-white shadow-lg shadow-[#4facfe]/30'
            : 'text-slate-400 hover:text-white'
        }`}
      >
        Investidor
      </button>
    </div>
  );
};

// ===================== CHAT =====================
const ChatMockup = () => {
  const [visible, setVisible] = useState(1);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let i = 1;
    const tick = () => {
      if (i < messages.length) {
        setTyping(true);
        setTimeout(() => {
          setTyping(false);
          i++;
          setVisible(i);
        }, 900);
      } else {
        // Reset after a pause
        setTimeout(() => { i = 1; setVisible(1); }, 3500);
      }
    };
    const id = setInterval(tick, 2300);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8 }}
      className="relative"
    >
      <div className="absolute -inset-6 bg-gradient-to-br from-[#4facfe]/15 to-[#ffa726]/10 rounded-3xl blur-2xl" />

      <div className="relative glass-card rounded-3xl overflow-hidden glow-blue">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"
          style={{ background: 'linear-gradient(180deg, rgba(20,25,50,0.5), transparent)' }}>
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-[#4facfe]/60"
              style={{ boxShadow: '0 0 16px rgba(79,172,254,0.5)' }}>
              <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face&q=80" alt="Roberto" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-sm font-bold text-white leading-tight">Roberto Cavalcanti</div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Online · Investidor
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#ffd54f] bg-[#ffa726]/15 border border-[#ffa726]/30 rounded-full px-2.5 py-1">
            <Sparkles size={11} /> Spark
          </div>
        </div>

        {/* Banner: Spark unlocked */}
        <div className="mx-5 mt-4 rounded-2xl px-4 py-3 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255,167,38,0.15), rgba(79,172,254,0.12))',
            border: '1px solid rgba(255,167,38,0.25)',
          }}
        >
          <div className="text-[10px] uppercase tracking-widest text-[#ffd54f] font-semibold">✨ Spark realizada ✨</div>
          <div className="text-xs text-slate-200 mt-0.5">Uma nova oportunidade começou. Conversem com carinho 💛</div>
        </div>

        {/* Messages */}
        <div className="px-5 py-5 space-y-2.5 min-h-[280px]">
          {messages.slice(0, visible).map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35 }}
              className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${
                  m.from === 'me'
                    ? 'rounded-br-md text-white'
                    : 'rounded-bl-md bg-white/[0.06] text-white border border-white/10'
                }`}
                style={m.from === 'me' ? {
                  background: 'linear-gradient(135deg, #4facfe, #1e90ff)',
                  boxShadow: '0 4px 16px rgba(79,172,254,0.35)',
                } : {}}
              >
                <div className="text-xs leading-relaxed">{m.text}</div>
                <div className={`flex items-center justify-end gap-1 mt-1 text-[9px] ${m.from === 'me' ? 'text-white/75' : 'text-slate-400'}`}>
                  {m.time}
                  {m.from === 'me' && <CheckCheck size={11} />}
                </div>
              </div>
            </motion.div>
          ))}
          {typing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-start"
            >
              <div className="bg-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1 border border-white/10">
                {[0, 1, 2].map(i => (
                  <motion.span
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                    className="w-1.5 h-1.5 rounded-full bg-white/80"
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Composer */}
        <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
          <button className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center">
            <ImageIcon size={14} />
          </button>
          <div className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs text-slate-500">
            Mande uma mensagem...
          </div>
          <button className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center">
            <Smile size={14} />
          </button>
          <button className="w-9 h-9 rounded-full flex items-center justify-center text-white"
            style={{ background: 'linear-gradient(135deg, #4facfe, #1e90ff)', boxShadow: '0 4px 14px rgba(79,172,254,0.4)' }}>
            <Send size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ===================== DASHBOARDS =====================
const EntrepreneurDashboard = () => {
  return (
    <div className="glass-card-gold rounded-3xl p-5 sm:p-6 glow-gold">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ffa726] to-[#ff7043] flex items-center justify-center font-bold text-black">B</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#ffd54f]">Painel da Beatriz</div>
            <div className="text-sm font-bold text-white leading-tight">Cafeteria Beatriz</div>
          </div>
        </div>
        <span className="text-[10px] text-green-400 bg-green-400/10 rounded-full px-2.5 py-1 font-semibold">ATIVO</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Metric icon={Eye} label="Visualizações" value={3284} suffix="" color="gold" />
        <Metric icon={Heart} label="Interessados" value={47} suffix="" color="gold" />
        <Metric icon={Sparkles} label="Sparks recebidas" value={12} suffix="" color="gold" featured />
        <Metric icon={MessageCircle} label="Mensagens" value={28} suffix="" color="gold" />
      </div>

      {/* Capt progress */}
      <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,167,38,0.2)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-[#ffd54f]">Captação</div>
          <div className="text-[10px] text-slate-400">68% da meta</div>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl heading-display font-bold gradient-text-gold">R$ <AnimatedNumber end={340} />k</div>
          <div className="text-xs text-slate-400">de R$ 500k</div>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '68%' }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #ffa726, #ff7043)' }}
          />
        </div>
      </div>

      {/* Latest Sparks */}
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Sparks recentes</div>
      <div className="space-y-2">
        {[
          { name: 'Roberto C.', amount: 'R$ 80.000', color: '#4facfe' },
          { name: 'Helena M.', amount: 'R$ 30.000', color: '#ffa726' },
          { name: 'Diego A.', amount: 'R$ 50.000', color: '#4facfe' },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
            className="glass-card rounded-xl px-3 py-2.5 flex items-center justify-between"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-black" style={{ background: s.color }}>
                {s.name[0]}
              </div>
              <div>
                <div className="text-xs text-white font-semibold">{s.name}</div>
                <div className="text-[9px] text-slate-400 flex items-center gap-1">
                  <Sparkles size={9} className="text-[#ffd54f]" /> Spark realizada
                </div>
              </div>
            </div>
            <div className="text-xs font-semibold text-[#ffd54f]">{s.amount}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const InvestorDashboardMock = () => {
  return (
    <div className="glass-card-blue rounded-3xl p-5 sm:p-6 glow-blue">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-[#4facfe]/60">
            <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face&q=80" alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4facfe]">Painel do Roberto</div>
            <div className="text-sm font-bold text-white leading-tight">Investidor</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-green-400 bg-green-400/10 rounded-full px-2.5 py-1">
          <ArrowUpRight size={11} /> +42%
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Metric icon={Sparkles} label="Sparks realizadas" value={8} suffix="" color="blue" featured />
        <Metric icon={Bookmark} label="Negócios salvos" value={23} suffix="" color="blue" />
        <Metric icon={TrendingUp} label="Acompanhando" value={14} suffix="" color="blue" />
        <Metric icon={Briefcase} label="Total investido" value={847} suffix="k" prefix="R$ " color="blue" />
      </div>

      {/* Chart */}
      <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(79,172,254,0.2)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-[#4facfe]">Crescimento</div>
          <div className="text-[10px] text-green-400 font-semibold">+42.7%</div>
        </div>
        <svg viewBox="0 0 100 35" className="w-full h-12">
          <defs>
            <linearGradient id="dash-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4facfe" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#4facfe" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            d="M 0 28 L 12 24 L 24 22 L 36 16 L 48 18 L 60 12 L 72 8 L 84 10 L 100 3"
            fill="none"
            stroke="#4facfe"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path d="M 0 28 L 12 24 L 24 22 L 36 16 L 48 18 L 60 12 L 72 8 L 84 10 L 100 3 L 100 35 L 0 35 Z" fill="url(#dash-area)" />
        </svg>
      </div>

      {/* Opportunities */}
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Oportunidades acompanhadas</div>
      <div className="space-y-2">
        {[
          { name: 'Cafeteria Beatriz', loc: 'São Paulo', amount: 'R$ 80k', spark: '96%' },
          { name: 'PetSpot Express', loc: 'BH', amount: 'R$ 120k', spark: '92%' },
          { name: 'Marcenaria Dani', loc: 'Curitiba', amount: 'R$ 45k', spark: '88%' },
        ].map((o, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
            className="glass-card rounded-xl px-3 py-2.5 flex items-center justify-between"
          >
            <div>
              <div className="text-xs text-white font-semibold">{o.name}</div>
              <div className="text-[9px] text-slate-400">{o.loc} · {o.amount}</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Spark</div>
              <div className="text-xs font-bold gradient-text">{o.spark}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, suffix = '', prefix = '', color, featured }) => {
  const isGold = color === 'gold';
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className={`rounded-2xl p-3.5 ${featured ? 'spotlight-card' : ''}`}
      style={{
        background: featured
          ? (isGold ? 'linear-gradient(135deg, rgba(255,167,38,0.18), rgba(10,14,30,0.5))' : 'linear-gradient(135deg, rgba(79,172,254,0.18), rgba(10,14,30,0.5))')
          : 'rgba(255,255,255,0.03)',
        border: `1px solid ${featured ? (isGold ? 'rgba(255,167,38,0.35)' : 'rgba(79,172,254,0.35)') : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon size={13} className={isGold ? 'text-[#ffd54f]' : 'text-[#4facfe]'} />
        {featured && <Sparkles size={11} className="text-[#ffd54f]" />}
      </div>
      <div className={`text-lg heading-display font-bold ${isGold ? 'gradient-text-gold' : 'gradient-text-blue'}`}>
        {prefix}<AnimatedNumber end={value} />{suffix}
      </div>
      <div className="text-[9px] text-slate-400 mt-0.5">{label}</div>
    </motion.div>
  );
};

export default ChatDashboardSection;
